import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, Stack, SxProps, Theme, Typography} from '@mui/material'
import {orderBy} from 'lodash'
import moment from 'moment-timezone'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {DowntimeByCategoryBarChart} from '../components'
import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../consts'
import {useStoppagesCategoryTypeCounts} from '../hooks'
import {StoppageSource, StoppageType} from '../types'
import {getStoppageCategoryCountPlaceHolders} from '../utils'

type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
}

export const KilnDowntimeByCategoryChartContainer = ({
  plantId,
  cardBoxSx,
  placeholderWrapperSx
}: Props) => {
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const startOfTheYear = moment.tz(timezone).startOf('year')
  const endOfTheYear = moment.tz(timezone).endOf('year')
  const currentYear = startOfTheYear.year()

  const {
    data = [],
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useStoppagesCategoryTypeCounts({
    plantId,
    from: startOfTheYear.toISOString(),
    to: endOfTheYear.toISOString(),
    stoppageSources: [StoppageSource.Janus],
    stoppageTypes: [StoppageType.Kiln]
  })

  const dataWithMonthPlaceholders = useMemo(() => {
    const placeholders = getStoppageCategoryCountPlaceHolders(data, currentYear)
    return orderBy([...data, ...placeholders], 'month')
  }, [data, currentYear])

  return (
    <CardBox
      sx={{display: 'flex', flexDirection: 'column', ...cardBoxSx}}
      data-test-id="kiln-downtime-by-category-card"
    >
      <Box display="flex" mb={2}>
        <Stack flex={1}>
          <Typography variant="h3">{t('kilnDowntimeByCategoryChart.cardTitle')}</Typography>
          <Typography variant="caption">{t('kilnDowntimeByCategoryChart.cardSubtitle')}</Typography>
        </Stack>
        <Chip icon={<CalendarTodayOutlined />} label={currentYear} size="small" sx={{ml: 1}} />
      </Box>
      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
      >
        <Box sx={{flex: 1, display: 'flex'}}>
          <DowntimeByCategoryBarChart
            data={dataWithMonthPlaceholders}
            labelVariant={'firstLetter'}
            abbreviatedLegendLabels={true}
          />
        </Box>
      </DataContentWrapper>
    </CardBox>
  )
}
